import {
  ConnectHarnessCredentialRequest,
  HarnessCredential,
  HarnessCredentialProvider,
} from "@cohesive/api-client";
import {
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from "@tanstack/react-query";
import { AxiosError } from "axios";
import { orgAPI } from "./api";

/**
 * Model provider credentials for the agent harness. Connecting and revoking are user-scoped only:
 * the org-scope surface is withheld for the initial rollout and the API refuses an org connect.
 * The list still spans both scopes — the user endpoint returns their own credentials plus the
 * organization's, which they inherit for any provider they have not connected themselves, so a
 * member sees what their turns actually spend.
 */

export type ConnectHarnessCredentialVariables =
  ConnectHarnessCredentialRequest & {
    provider: HarnessCredentialProvider;
  };

const invalidate = (queryClient: ReturnType<typeof useQueryClient>) => {
  queryClient.invalidateQueries({ queryKey: ["harnessCredentials"] });
  // Which harnesses are available is derived from these credentials, so the chat selector's
  // answer goes stale the moment one is connected or revoked.
  queryClient.invalidateQueries({ queryKey: ["harnessCapabilities"] });
};

export function useHarnessCredentials(
  options?: Partial<UseQueryOptions<HarnessCredential[], AxiosError>>,
) {
  return useQuery<HarnessCredential[], AxiosError>({
    queryKey: ["harnessCredentials"],
    queryFn: async () => {
      return await orgAPI.harnessCredentials.listUserHarnessCredentials();
    },
    ...options,
  });
}

export function useConnectHarnessCredential(
  options?: UseMutationOptions<
    HarnessCredential,
    AxiosError,
    ConnectHarnessCredentialVariables
  >,
) {
  const queryClient = useQueryClient();
  return useMutation<
    HarnessCredential,
    AxiosError,
    ConnectHarnessCredentialVariables
  >({
    mutationFn: async ({ provider, ...data }) => {
      return await orgAPI.harnessCredentials.connectUserHarnessCredential(
        provider,
        data,
      );
    },
    onSuccess: () => invalidate(queryClient),
    ...options,
  });
}

export function useRevokeHarnessCredential(
  options?: UseMutationOptions<void, AxiosError, HarnessCredential>,
) {
  const queryClient = useQueryClient();
  return useMutation<void, AxiosError, HarnessCredential>({
    mutationFn: async (credential) => {
      await orgAPI.harnessCredentials.revokeUserHarnessCredential(
        credential.id,
      );
    },
    onSuccess: () => invalidate(queryClient),
    ...options,
  });
}
